import dayjs from 'dayjs';
import type { RailArrival, RailDeparture, RailStationLink, RailTrain } from '@/@types/rail';
import type { IconName } from '@/libs/icons';

/** GTFS route_type of a road replacement coach (`Car TER`). */
const ROUTE_TYPE_BUS = 3;

/** What a train card shows for its time column. */
export interface RailTrainTime {
  /** HH:MM — the estimate when realtime covers the train, the timetable otherwise. */
  time: string;
  type: 'realtime' | 'theoretical';
  /** Delay rounded to the minute, only on a `delayed` train. */
  delayMinutes?: number;
  /** Timetable time a delayed train no longer honours, shown struck through under the estimate. */
  scheduledTime?: string;
  isCanceled: boolean;
}

/**
 * Stops attached to a station whose board is served. An orphan link points to a stop
 * gone from the published GTFS; a `pending` or `not_found` one to a station the rail
 * store has no departures for, so the line would open onto a board empty for good.
 */
export function railLinkedStopIds(links: Array<RailStationLink>): Set<string> {
  return new Set(
    links.filter(link => !link.is_orphan && link.ingestion_status === 'ready').map(link => link.stop_id),
  );
}

/**
 * A GTFS station (location_type 1) is attached when the station itself or one of
 * its child stops is — the same set the stop sheet reads its departures from.
 */
export function isRailLinkedStop(
  stopId: string,
  linkedStopIds: Set<string>,
  stations: { [stationId: string]: Array<string> },
): boolean {
  if (linkedStopIds.has(stopId)) return true;
  return (stations[stopId] ?? []).some(childId => linkedStopIds.has(childId));
}

export function railTrainIcon(train: RailTrain): IconName {
  return train.route_type === ROUTE_TYPE_BUS ? 'bus-front' : 'train-front';
}

function formatRailTime(isoDateTime: string): string {
  return dayjs(isoDateTime).format('HH:mm');
}

export function formatRailDepartureTime(departure: RailDeparture): RailTrainTime {
  return formatRailTrainTime(departure, departure.scheduled_departure, departure.estimated_departure);
}

export function formatRailArrivalTime(arrival: RailArrival): RailTrainTime {
  return formatRailTrainTime(arrival, arrival.scheduled_arrival, arrival.estimated_arrival);
}

/** The same rules on both boards, read off the time of the board the train is on. */
function formatRailTrainTime(
  train: RailTrain,
  scheduledAt: string,
  estimatedAt?: string | null,
): RailTrainTime {
  const scheduled = formatRailTime(scheduledAt);
  const estimated = estimatedAt ? formatRailTime(estimatedAt) : scheduled;

  switch (train.status) {
    case 'canceled':
    case 'skipped':
      return { time: scheduled, type: 'theoretical', isCanceled: true };
    case 'delayed': {
      const delayMinutes = Math.round(railDelaySeconds(train, scheduledAt, estimatedAt) / 60);
      // Under a minute the estimate carries no visible delay: shown as an on-time train, never "+0 min"
      if (delayMinutes < 1) return { time: estimated, type: 'realtime', isCanceled: false };
      return { time: estimated, type: 'realtime', delayMinutes, scheduledTime: scheduled, isCanceled: false };
    }
    case 'on_time':
    case 'early':
    case 'added':
      return { time: estimated, type: 'realtime', isCanceled: false };
    case 'scheduled':
    default:
      return { time: scheduled, type: 'theoretical', isCanceled: false };
  }
}

// The feed sometimes states a status without the matching delay: fall back on the
// two times rather than lose the delay of a train flagged late.
function railDelaySeconds(train: RailTrain, scheduledAt: string, estimatedAt?: string | null): number {
  if (train.delay_seconds != null) return train.delay_seconds;
  if (!estimatedAt) return 0;
  return dayjs(estimatedAt).diff(dayjs(scheduledAt), 'second');
}
